import { afterNextRender, DestroyRef, Directive, ElementRef, inject, input, signal } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';

/**
 * Applied to a `position: sticky` element along with the element it must never cover: the host
 * slides and fades out of the pinned band once that element scrolls up into it, and returns on the
 * way back down. It stays sticky throughout because `position` cannot be transitioned, and dropping
 * out of the band would snap it up to its layout position with no way to animate the change.
 *
 * The point it stepped aside at is remembered as a scroll position because the host's own rect no
 * longer describes the band once it has been translated out of it.
 */
@Directive({
  selector: '[rmaYieldTo]',
  host: {
    class: 'transition-[translate,opacity] duration-200 ease-out motion-reduce:transition-none',
    '[class.-translate-y-full]': '!inBand()',
    '[class.opacity-0]': '!inBand()',
    '[class.pointer-events-none]': '!inBand()',
  },
})
export class YieldToDirective {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly window = inject(WA_WINDOW);

  protected readonly inBand = signal(true);

  public readonly yieldTo = input<HTMLElement | undefined>(undefined, { alias: 'rmaYieldTo' });

  private yieldedAtScrollY = 0;

  constructor() {
    const destroyRef = inject(DestroyRef);

    // afterNextRender only runs in the browser, so the listener stays SSR-safe.
    afterNextRender(() => {
      const onScroll = () => this.measure();

      this.window.addEventListener('scroll', onScroll, { passive: true });
      destroyRef.onDestroy(() => this.window.removeEventListener('scroll', onScroll));

      this.measure();
    });
  }

  private measure(): void {
    const yieldTo = this.yieldTo();

    if (!yieldTo) {
      this.inBand.set(true);
      return;
    }

    if (!this.inBand()) {
      if (window.scrollY < this.yieldedAtScrollY) {
        this.inBand.set(true);
      }

      return;
    }

    const yieldToRect = yieldTo.getBoundingClientRect();

    // Nothing to make way for until the other element has been laid out.
    if (!yieldToRect.height) {
      return;
    }

    if (yieldToRect.top <= this.host.nativeElement.getBoundingClientRect().bottom) {
      this.yieldedAtScrollY = window.scrollY;
      this.inBand.set(false);
    }
  }
}
